import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useT } from '@/i18n';
import {
  HOUSEHOLD_RELATIONSHIP_TYPES,
  type HouseholdRelationshipType,
} from '@/features/screening/household.api';

const RELATIONSHIP_TYPE_SET = new Set<string>(HOUSEHOLD_RELATIONSHIP_TYPES);

/** Map a free-string relationship into the typed enum or null. Centralizes the
 *  narrowing the strongly-typed i18n keys require. */
function asRelationshipType(input: string | null | undefined): HouseholdRelationshipType | null {
  if (input == null || input === '' || !RELATIONSHIP_TYPE_SET.has(input)) return null;
  return input as HouseholdRelationshipType;
}

/**
 * Cluster H — banner shown in ClientProfilePage header when this client was
 * created via household_promotion (clients.created_from_household_member_id
 * IS NOT NULL).
 *
 * Backend exposes the metadata in ClientProfile.promotedFrom (Agent D
 * extension to clients.service.getProfile). Hidden for clients with
 * created_via='upload_batch' (default) or any other provenance.
 */
export interface PromotedBannerProps {
  parentClientName: string | null;
  parentScreeningId: string | null;
  promotedAt: string | null;
  promotedByName: string | null;
  promotedByEmail: string | null;
  /** v0.6.0 Fase 11.13 — when true, the "Ver screening padre" link is
   *  hidden. Used by uploader review modal because uploader role has no
   *  access to /screenings/:id (would 403). Info display still renders. */
  hideViewLink?: boolean;
  /** v0.6.1 Fase 4 — when set, renders "Relación: <label>" line.
   *  Source: household_members.relationship_type. The backend wiring
   *  through clients.service.getProfile().promotedFrom is a v0.6.2
   *  follow-up; today this prop is wired through where the caller
   *  already has the value (e.g. uploader review modal). */
  relationship?: string | null;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function PromotedBanner({
  parentClientName,
  parentScreeningId,
  promotedAt,
  promotedByName,
  promotedByEmail,
  hideViewLink = false,
  relationship = null,
}: PromotedBannerProps): JSX.Element {
  const t = useT();
  const agentLabel = promotedByName ?? promotedByEmail ?? '—';
  const dateLabel = fmtDate(promotedAt);
  const bodyKey = parentClientName
    ? 'clientProfile.promotedBanner.body'
    : 'clientProfile.promotedBanner.bodyNoParent';
  // v0.6.1 Fase 4 — translation key per relationship enum value. Casting
  // through asRelationshipType() narrows to the i18n key union.
  const narrowed = asRelationshipType(relationship);
  const relationshipLabel = narrowed
    ? t(`screening.household.relationship.${narrowed}` as const)
    : null;

  return (
    <div
      role="status"
      className="rounded-lg border border-peach-300 bg-peach-50 px-4 py-3 flex items-start gap-3"
    >
      <Sparkles className="h-4 w-4 text-peach-700 shrink-0 mt-0.5" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-peach-900">
          {t('clientProfile.promotedBanner.title')}
        </p>
        <p className="text-xs text-peach-800 mt-0.5">
          {t(bodyKey, {
            parent: parentClientName ?? '',
            agent: agentLabel,
            date: dateLabel,
          })}
        </p>
        {relationshipLabel && (
          <p className="text-xs text-peach-800 mt-0.5">
            <span className="font-medium">{t('clientProfile.promotedBanner.relationshipLabel')}:</span>{' '}
            {relationshipLabel}
          </p>
        )}
        {parentScreeningId && !hideViewLink && (
          <Link
            to={`/screenings/${parentScreeningId}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-peach-900 underline mt-1.5 hover:no-underline"
          >
            {t('clientProfile.promotedBanner.viewParentLink')}
            <ArrowRight className="h-3 w-3" aria-hidden />
          </Link>
        )}
      </div>
    </div>
  );
}
